/**
 * Synthesize a `GameRow` from a single `SpreadLine` / `TotalLine` so the
 * existing `GameDetailPanel` + `TradeModal` (both pinned to GameRow's
 * two-team shape) can render a single-binary-market view.
 *
 * The away/home slots both reference the SAME underlying VenueBook. The
 * panel's team selector becomes a no-op (both tabs show identical data),
 * which is benign for v1; order placement always goes to the underlying
 * ticker with the user-selected YES/NO side. Side labels carry the
 * relevant context — "Arizona Diamondbacks -5.5" / "Texas Rangers +5.5"
 * for spreads, "Over 9.5" / "Under 9.5" for totals.
 */

import type { GameEventRow, GameRow, SpreadLine, TotalLine, VenueBook } from '../ValueDashboard/types';

export function primaryLineBook(line: SpreadLine | TotalLine): VenueBook | null {
  return line.kalshi ?? line.polymarket ?? null;
}

export function spreadLineKey(line: SpreadLine): string {
  return line.kalshi?.marketTicker ?? line.polymarket?.marketTicker ?? `spread-${line.favored}-${line.line}`;
}

export function totalLineKey(line: TotalLine): string {
  return line.kalshi?.marketTicker ?? line.polymarket?.marketTicker ?? `total-${line.line}`;
}

export function linePriceCents(line: SpreadLine | TotalLine): number | null {
  return primaryLineBook(line)?.priceCents ?? null;
}

function formatSignedMag(mag: number, sign: '+' | '−'): string {
  return `${sign}${mag}`;
}

export function synthesizeFromSpread(event: GameEventRow, line: SpreadLine): GameRow {
  const mag = Math.abs(line.line);
  const awaySign: '+' | '−' = line.favored === 'away' ? '−' : '+';
  const homeSign: '+' | '−' = line.favored === 'home' ? '−' : '+';
  return {
    key: `${event.key}|spread|${spreadLineKey(line)}`,
    sport: event.sport,
    date: event.date,
    timePt: '',
    // Carry the event's scheduled start through so the GameDetailPanel's
    // GTS / countdown / near-start-pulse logic works against synth
    // GameRows the same way it does against moneyline rows. Nulling
    // this was the cause of a "Good to Start unavailable" message
    // showing on tomorrow's CHC|HOU spread/total tickets.
    startTimeMs: event.startTimeMs,
    gameState: event.gameState, // carry the real live/pre/final state so the LIVE badge + live-order warning fire on spread/total markets too
    finalWinnerSide: null,
    awayCode: event.awayCode,
    homeCode: event.homeCode,
    awayName: `${event.awayName} ${formatSignedMag(mag, awaySign)}`,
    homeName: `${event.homeName} ${formatSignedMag(mag, homeSign)}`,
    kalshi: line.kalshi
      ? { away: line.kalshi, home: line.kalshi }
      : null,
    polymarket: line.polymarket
      ? { away: line.polymarket, home: line.polymarket }
      : null,
  };
}

export function synthesizeFromTotal(event: GameEventRow, line: TotalLine): GameRow {
  return {
    key: `${event.key}|total|${totalLineKey(line)}`,
    sport: event.sport,
    date: event.date,
    timePt: '',
    startTimeMs: event.startTimeMs,
    gameState: event.gameState, // carry the real live/pre/final state so the LIVE badge + live-order warning fire on spread/total markets too
    finalWinnerSide: null,
    awayCode: event.awayCode,
    homeCode: event.homeCode,
    awayName: `Over ${line.line}`,
    homeName: `Under ${line.line}`,
    kalshi: line.kalshi
      ? { away: line.kalshi, home: line.kalshi }
      : null,
    polymarket: line.polymarket
      ? { away: line.polymarket, home: line.polymarket }
      : null,
  };
}
